import axios from 'axios';
import * as url from 'url';
import * as _ from 'lodash';
import * as Promise from 'bluebird';
import { I18n } from 'react-redux-i18n-lite';
import dayjs from 'dayjs';
import { prettyName } from '../utils/stringUtils';
import * as FileSystemService from '../services/file/fileSystemService';
import * as contentService from '../services/domain/contentService';
import * as messageAlert from './messageAlertAction';
import * as contentUtils from '../utils/contentUtils';
import * as contentPathUtils from '../utils/contentPathUtils';
import {
  ACTIVITY_TYPE,
  BOOK_DOWNLOADING_STATUS,
  BOOK_LAUNCH_MODES,
  CDN_SYNCHRONIZATION_STATUS,
  CONTENT_SYNCHRONIZATION_STATUS,
  DOWNLOAD,
  FAILED_TO_FETCH_ERROR,
  FILE_TYPES
} from '../const';
import { API_CLASSROOMS } from '../services/api/apiConst';
import { REDUCER } from '../reducers/reducersConst';
import { isElectronApp, requireFromElectron } from '../services/crossPlatform/crossPlatformService';
import { deleteBookPostAction } from '../actions/cross-platform/book/bookActions.electron';
import { getLogger } from '../services/logger';
import { getBooks } from '../services/api/bookApi';
import { translateObject } from '../services/translate/customTranslateService';
import * as jwtService from '../services/jwt/jwtService';
import { getTokenFromStorage } from '../shared';
import { sendDownloadingOrUnzippingBooks, sendDownloadingOrUnzippingCdnOrSkin } from '../electron/ipcNotifications';

const fs = requireFromElectron('fs-extra');

const debug = getLogger('actions:contentActions');

/**
 * Saves the content book to download (store)
 * @param contentBook
 * @returns {{type: string, contentBook: Object}}
 */
export let setContentBook = (contentBook = {}) => {
  return {
    type: REDUCER.CONTENT.SET_CONTENT_BOOK,
    contentBook: JSON.parse(JSON.stringify(contentBook))
  };
};

/**
 * Sets synchronizing smart content process state in store
 * @param contentSynchronizationStatus
 * @returns {{type: string, contentSynchronizationStatus: number}}
 */
export let setContentSynchronization = contentSynchronizationStatus => {
  return {
    type: REDUCER.CONTENT.SET_CONTENT_SYNCHRONIZATION,
    contentSynchronizationStatus
  };
};

/**
 * Sets synchronizing cdn process state in store
 * @param cdnSynchronizationStatus
 * @returns {{type: string, cdnSynchronizationStatus: number}}
 */
export let setCdnSynchronization = cdnSynchronizationStatus => {
  return {
    type: REDUCER.CONTENT.SET_CDN_SYNCRHONIZATION,
    cdnSynchronizationStatus
  };
};

/**
 * Sets last content synchronization date in store
 * @param contentSynchronizationDate
 * @returns {{type: string, contentSynchronizationDate: object}}
 */
let setContentSynchronizationDate = contentSynchronizationDate => {
  return {
    type: REDUCER.CONTENT.SET_CONTENT_SYNCHRONIZATION_DATE,
    contentSynchronizationDate
  };
};

/**
 * Sets activityContent in store
 * @param activityContent
 * @returns {{type: string, activityContent: object}}
 */
export let setActivityContent = activityContent => {
  return {
    type: REDUCER.CONTENT.SET_ACTIVITY_CONTENT,
    activityContent
  };
};

/**
 *
 * @param classroomId
 * @returns {function(*)}
 */
export let saveClassroom = classroomId => {
  return (dispatch, getState) => {
    const uri = url.resolve(`${API_CLASSROOMS}/`, `${classroomId}/books`);
    return axios.get(uri).then(res => {
      const state = getState();
      const language = state.account.user.language;
      const books = res.data;
      return Promise.map(books, book => {
        const bookName = translateObject(JSON.parse(book.name), language);
        return dispatch(saveBook(classroomId, book, bookName));
      }).catch(err => {
        debug.error('[saveClassroom] %s', err);
      });
    });
  };
};

export let reDownloadBook = (classId, book, bookName) => {
  return async dispatch => {
    try {
      dispatch(saveBook(classId, book, bookName, true));
    } catch (error) {
      console.error(error);
    }
  };
};

export let saveBook = (classId, book, bookName, reDownloadBook = false, updatedResources = [], bookCmi5) => {
  return async (dispatch, getState) => {
    const store = getState();
    const user = store.account.user;
    const isAppOnline = store.connection.status.online;
    const singleUnitModeUnitId = store.router.location.query.unitId;
    let storeContentBook = getState().content.contentBooks[book.id];

    if (!isAppOnline) {
      return;
    }

    let bookActivityId = book.activityId ? book.activityId : book.bookId;

    const values = {
      id: book.id,
      fromCatalog: book.fromCatalog,
      bookId: bookActivityId,
      classId,
      bookName,
      status: BOOK_DOWNLOADING_STATUS.DOWNLOADING
    };
    let contentBook = contentUtils.createContentBookStructure(values);

    const userId = user._id;

    const bookContext = contentUtils.createBookContext(userId, classId, book.id, bookActivityId, bookName);

    try {
      if (contentUtils.isContentBookDownloadingOrUnzipping(storeContentBook)) {
        return;
      }
      await dispatch(createOrUpdateContentBook(contentBook, reDownloadBook));
      const {
        content: { contentBooks }
      } = getState();
      sendDownloadingOrUnzippingBooks(contentBooks);
      if (!bookCmi5) {
        bookCmi5 = await contentUtils.getFilteredCmi5ResponseByUnitId(
          bookContext.classId,
          bookContext.bookId,
          bookContext.bookActivityId,
          singleUnitModeUnitId
        );
      }
      const isBookContentEmpty = contentUtils.isEmptyContent(bookCmi5);
      if (isBookContentEmpty) {
        let bookNamePretty = prettyName(bookName, 38);
        dispatch(messageAlert.info(`${bookNamePretty} ${I18n.t('common.bookIsEmpty')}`));
        dispatch(updateContentBookStatus(BOOK_DOWNLOADING_STATUS.EMPTY, contentBook.id, contentBook.classId));
        debug.error('[saveBook] Empty bookCmi5 or bookCmi5.auOrBlock for bookContext %O', bookContext);
        return;
      }

      const isBookContentDownloaded = FileSystemService.isBookContentDownloaded(bookContext, bookCmi5);
      // Only saves already downloaded book if this method is called by re-download function
      if (
        contentUtils.isContentBookDownloadingOrUnzipping(storeContentBook) ||
        (isBookContentDownloaded && !reDownloadBook && updatedResources.length === 0)
      ) {
        return FileSystemService.saveOrUpdateOfflineCmi5(bookContext, bookCmi5);
      }

      if (singleUnitModeUnitId) {
        const unitContext = contentUtils.createUnitContext(
          userId,
          classId,
          book.id,
          bookActivityId,
          singleUnitModeUnitId,
          bookName
        );
        const isUnitContentDownloaded = FileSystemService.isUnitContentDownloaded(unitContext, bookCmi5);
        const { hasUpdates } = FileSystemService.isBookOutdated(bookCmi5, bookContext, singleUnitModeUnitId);
        if (isUnitContentDownloaded && !reDownloadBook && hasUpdates.length === 0) {
          await dispatch(
            updateContentBookStatus(BOOK_DOWNLOADING_STATUS.COMPLETED, contentBook.id, contentBook.classId)
          );
          return FileSystemService.saveOrUpdateOfflineCmi5(bookContext, bookCmi5);
        }
      } else {
        try {
          if (reDownloadBook) {
            FileSystemService.remove(contentPathUtils.getBookFolderPath(bookContext));
          }
        } catch (err) {
          dispatch(updateContentBookStatus(BOOK_DOWNLOADING_STATUS.COMPLETED, contentBook.id, contentBook.classId));
          dispatch(messageAlert.error(I18n.t('error.redownloadError')));
        }
      }

      localStorage.removeItem(`saveBook_${bookContext.bookActivityId}`);
      bookCmi5 = await FileSystemService.saveOrUpdateOfflineCmi5(bookContext, bookCmi5);

      debug('[saveBook] book content is downloading');
      await dispatch(
        saveBookContents(bookCmi5.auOrBlock, bookContext, singleUnitModeUnitId, reDownloadBook, updatedResources)
      );
      debug('[saveBook] book content downloaded');

      if (!singleUnitModeUnitId) {
        return await contentService.writeBookDownloadCheckFile(bookContext);
      }
    } catch (err) {
      debug.error('[saveBook] error saving book %s', err);
      await dispatch(deleteBookPostAction(classId, book.id, bookActivityId, userId));
      dispatch(updateContentBookStatus(BOOK_DOWNLOADING_STATUS.EMPTY, contentBook.id, contentBook.classId));
      let errorMessage = I18n.t('error.saveBook');
      if (err.responseStatusCode === 404) {
        errorMessage = `${I18n.t('error.resourceNotFound')} ${err.activityName}`;
      }
      dispatch(messageAlert.error(errorMessage));
    }
  };
};

let bookContentDownloaded = (contentBook, singleUnitModeUnitId) => {
  return async (dispatch, getState) => {
    dispatch(updateContentBookStatus(BOOK_DOWNLOADING_STATUS.COMPLETED, contentBook.bookId, contentBook.classId));

    let bookName = prettyName(contentBook.bookName, 38);
    let textAlert = singleUnitModeUnitId ? I18n.t('common.unitSaved') : `${bookName} ${I18n.t('common.bookSaved')}`;
    dispatch(messageAlert.info(textAlert));
  };
};

export const removeBookAndResetDownloadProgress = (contentBook, userId) => {
  return async (dispatch, getState) => {
    const { classId, id, bookId } = contentBook;
    const singleUnitModeUnitId = getState().router.location.query.unitId;
    await dispatch(resetContentBookDownloadVariables(id, classId));
    FileSystemService.removeDownloadingContent(userId, classId, id, bookId, singleUnitModeUnitId);
  };
};

export let cancelBookDownload = (contentBook, userId, showMessage = true) => {
  return async (dispatch, getState) => {
    const { classId, id } = contentBook;
    await dispatch(updateContentBookStatus(BOOK_DOWNLOADING_STATUS.CANCEL, id, classId));
    await dispatch(removeBookAndResetDownloadProgress(contentBook, userId));

    const bookName = prettyName(contentBook.bookName, 38);
    if (showMessage) {
      await dispatch(messageAlert.error(I18n.t('common.canceledDownload', { title: bookName })));
    }
  };
};

/**
 * Creates (or updates if reset variable it is true) contentBook from the content books array (store)
 * @param contentBook
 * @param resetContentBook - force reset
 * @returns {function(*, *)}
 */
let createOrUpdateContentBook = (contentBook, resetContentBook = false) => {
  return (dispatch, getState) => {
    const state = getState();
    const { id, bookId, classId } = contentBook;
    if (!id && !bookId && !classId) return;

    const storeContentBook = state.content.contentBooks[contentBook.id];
    const contentEmpty = storeContentBook ? storeContentBook.status === BOOK_DOWNLOADING_STATUS.EMPTY : false;
    const contentCanceled = storeContentBook ? storeContentBook.status === BOOK_DOWNLOADING_STATUS.CANCEL : false;
    if (!storeContentBook || resetContentBook || contentEmpty || contentCanceled) {
      return dispatch(setContentBook(contentBook));
    }
  };
};

export let updateContentBookStatus = (status, bookId, classId) => {
  return async (dispatch, getState) => {
    const state = getState();
    const storedContentBooks = state.content.contentBooks;
    if (!bookId || !classId) return;
    const updateContentBook = await contentUtils.getContentBookByBookIdAndClassId(storedContentBooks, bookId, classId);
    if (!updateContentBook) return;
    /** Updates only contentBook downloading status **/
    updateContentBook.status = status;

    dispatch(setContentBook(updateContentBook));
    const {
      content: { contentBooks }
    } = state;
    sendDownloadingOrUnzippingBooks(contentBooks);
  };
};

export let updateUnzippingStatusWhereApplicable = (classId, bookId = null) => {
  return (dispatch, getState) => {
    if (!classId) return;

    const contentBook = contentUtils.getBooksDownloadingByBookIdAndClassId(
      getState().content.contentBooks[bookId],
      classId
    );

    if (contentBook.status !== BOOK_DOWNLOADING_STATUS.DOWNLOADING) return;

    const bookActivitiesContent = contentUtils.getBookActivitiesContent(
      contentBook,
      getState().content.activitiesContent
    );
    const allBookContentsAreUnzipping = contentUtils.areAllBookContentsUnzipping(bookActivitiesContent);

    debug('[updateUnzippingStatusWhereApplicable] allBookContentsAreUnzipping', allBookContentsAreUnzipping);

    if (allBookContentsAreUnzipping) {
      dispatch(updateContentBookStatus(BOOK_DOWNLOADING_STATUS.UNZIPPING, bookId, contentBook.classId));
    }
  };
};

export let pauseContentBooksDownloads = () => {
  return async (dispatch, getState) => {
    const storedContentBooks = getState().content.contentBooks;
    const userId = getState().account.user._id;
    const booksDownloading = contentUtils.getAllBooksDownloading(storedContentBooks);

    booksDownloading.forEach(book => {
      book.status = BOOK_DOWNLOADING_STATUS.CANCEL;
      dispatch(removeBookAndResetDownloadProgress(book, userId));
      dispatch(setContentBook(book));
    });
    if (booksDownloading.length) {
      await dispatch(messageAlert.error(I18n.t('common.canceledDownloadDisconnection')));
    }
  };
};

export let resetContentBookDownloadVariables = (bookId, classId) => {
  return (dispatch, getState) => {
    const state = getState();
    const storedContentBooks = state.content.contentBooks;
    const activitiesContent = state.content.activitiesContent;

    let contentBook = contentUtils.getContentBookByBookIdAndClassId(storedContentBooks, bookId, classId);
    const bookActivitiesContent = contentUtils.getBookActivitiesContent(contentBook, activitiesContent);

    localStorage.removeItem(`saveBook_${contentBook.id}`);
    if (!contentBook) return;

    bookActivitiesContent.forEach(activityContent => {
      contentUtils.resetDownloadProps(activityContent);
      dispatch(setActivityContent(activityContent));
    });
    dispatch(setContentBook(contentBook));
  };
};

export let updateContentBookIsSynchronizing = (contentBook, isSynchronizing) => {
  return dispatch => {
    if (contentBook) {
      contentBook.isSynchronizing = isSynchronizing;
      dispatch(setContentBook(contentBook));
    }
  };
};

/**
 * Set up cmi5etags of content books array (store)
 * @param contentBooksFromLocalStorage
 * @returns {function(*, *)}
 */
export let setContentBookCmi5etags = contentBooksFromLocalStorage => {
  return dispatch => {
    if (contentBooksFromLocalStorage) {
      contentBooksFromLocalStorage.forEach(cb => {
        const contentBook = contentUtils.createContentBookStructure();
        contentBook.id = cb.id;
        contentBook.isCustomBook = cb.isCustomBook;
        contentBook.bookId = cb.bookId;
        contentBook.classId = cb.classId;
        contentBook.bookName = cb.bookName;
        contentBook.cmi5etag = cb.cmi5etag;
        contentBook.status = BOOK_DOWNLOADING_STATUS.EMPTY;

        dispatch(setContentBook(contentBook));
      });
    }
  };
};

function updateDownloadedBooksStatusOnStore(userId, classrooms) {
  return async (dispatch, getState) => {
    try {
      return Promise.map(classrooms, classroom => {
        return getBooks(classroom.id).then(res => {
          let books = res.data;
          return Promise.map(books, async book => {
            const bookContext = contentUtils.createBookContext(userId, classroom.id, book.id, book.activityId);
            const singleUnitModeUnitId = getState().router.location.query.unitId;
            let contentBookOrUnitIsDownloaded = FileSystemService.isBookContentDownloaded(bookContext);
            if (singleUnitModeUnitId) {
              const unitContext = contentUtils.createUnitContext(
                userId,
                classroom.id,
                book.id,
                book.activityId,
                singleUnitModeUnitId
              );
              contentBookOrUnitIsDownloaded = FileSystemService.isUnitContentDownloaded(unitContext);
            }
            if (contentBookOrUnitIsDownloaded) {
              const contentBooks = getState().content.contentBooks;
              const storeContentBook = contentUtils.getContentBookByBookIdAndClassId(
                contentBooks,
                book.id,
                classroom.id
              );

              if (storeContentBook) {
                dispatch(updateContentBookStatus(BOOK_DOWNLOADING_STATUS.COMPLETED, book.id, classroom.id));
              } else {
                const language = getState().account.user.language;
                const bookName = translateObject(book.name, language);
                let contentBook = contentUtils.newContentBook(book, classroom.id, bookName);
                contentBook.status = BOOK_DOWNLOADING_STATUS.COMPLETED;
                dispatch(createOrUpdateContentBook(contentBook, true));
              }
            }
            return Promise.resolve({});
          });
        });
      }).then(() => {
        dispatch(setContentSynchronizationDate(dayjs()));
        dispatch(setContentSynchronization(CONTENT_SYNCHRONIZATION_STATUS.COMPLETED));
        sendDownloadingOrUnzippingBooks(null, false);
        return Promise.resolve({});
      });
    } catch (err) {
      dispatch(setContentSynchronization(CONTENT_SYNCHRONIZATION_STATUS.COMPLETED));
      sendDownloadingOrUnzippingBooks(null, false);
      debug.error('[updateDownloadedBooksStatusOnStore] error %s', err);
      return Promise.reject(err);
    }
  };
}

/**
 * Synchronizes the store with the downloaded content
 * @param userId
 * @returns {function(*=)}
 */
export let synchronizeContentDownload = (userId, forceCdnDownload) => {
  sendDownloadingOrUnzippingBooks(null, true);
  return async (dispatch, getState) => {
    try {
      const content = getState().content;
      const authToken = getTokenFromStorage();
      const customerId = jwtService.getUserCustomerId(authToken);

      if (!isElectronApp() || !userId || _.isEmpty(authToken)) return;
      if (contentUtils.isContentAlreadyDownloading(content)) {
        debug('[synchronizeContentDownload] Process is already running');
        return;
      }

      dispatch(setContentSynchronization(CONTENT_SYNCHRONIZATION_STATUS.SYNCHRONIZING));

      dispatch(updateSkinAndCdn(authToken, userId, customerId, forceCdnDownload));
      const isAppOnline = getState().connection.status.online;
      const classrooms = getState().classrooms.activeClassrooms;

      if (!isAppOnline) {
        return dispatch(updateDownloadedBooksStatusOnStore(userId, classrooms));
      }

      try {
        Promise.map(classrooms, classroom => {
          return dispatch(synchronizeClassroomBooks(userId, classroom.id, isAppOnline, authToken));
        }).then(() => {
          dispatch(setContentSynchronizationDate(dayjs()));
          dispatch(setContentSynchronization(CONTENT_SYNCHRONIZATION_STATUS.COMPLETED));
          sendDownloadingOrUnzippingBooks(null, false);
          Promise.resolve({});
        });
      } catch (err) {
        debug.error('[synchronizeContentDownload] error %s', err);
        sendDownloadingOrUnzippingBooks(null, false);
        dispatch(setContentSynchronization(CONTENT_SYNCHRONIZATION_STATUS.COMPLETED));
      }
    } catch (err) {
      debug.error('[synchronizeContentDownload] error %s', err);
      sendDownloadingOrUnzippingBooks(null, false);
      dispatch(setContentSynchronization(CONTENT_SYNCHRONIZATION_STATUS.COMPLETED));
      throw err;
    }
  };
};

function synchronizeClassroomBooks(userId, classroomId, isAppOnline, authToken) {
  return async (dispatch, getState) => {
    const user = getState().account.user;
    const language = user.language;
    const userId = user._id;

    if (!isAppOnline || !isElectronApp()) return;
    if (_.isEmpty(authToken)) return;

    return await getBooks(classroomId)
      .then(res => {
        let books = res.data;

        FileSystemService.removeObsoleteDownloadedClassroomBooks(userId, classroomId, books);

        return Promise.map(books, async book => {
          try {
            const bookName = translateObject(book.name, language);
            const contentBook = contentUtils.getContentBook(getState, book, classroomId, bookName);
            await dispatch(createOrUpdateContentBook(contentBook));
            await dispatch(updateContentBookIsSynchronizing(contentBook, true));
            return dispatch(synchronizeBookDownload(book, classroomId));
          } catch (err) {
            debug.error('[synchronizeClassroomBooks] error dispatching book download %s', err);
          }
        });
      })
      .catch(err => {
        debug.error('[synchronizeClassroomBooks] error getting the books', err);
      });
  };
}

export function synchronizeBookDownload(book, classroomId, unitActivityId, withCatalogContent = true) {
  return async (dispatch, getState) => {
    const state = getState();
    const isAppOnline = state.connection.status.online;
    const authToken = getTokenFromStorage();
    const user = state.account.user;
    const language = user.language;
    const userId = user._id;
    const bookName = translateObject(book.name, language);
    const singleUnitModeUnitId = state.router.location.query.unitId;
    const customerId = state.account.configuration.customerId;
    const bookContext = contentUtils.createBookContext(
      userId,
      classroomId,
      book.id,
      book.activityId,
      bookName,
      customerId
    );
    const contentBook = state.content.contentBooks[book.id];

    try {
      let bookCmi5;
      const bookPath = contentPathUtils.getBookFolderPath(bookContext);
      const bookDownloadedOrInProgress = FileSystemService.existsFolder(bookPath);

      if (bookDownloadedOrInProgress) {
        bookCmi5 = await contentUtils.getFilteredCmi5ResponseByUnitId(
          classroomId,
          book.id,
          book.activityId,
          singleUnitModeUnitId,
          withCatalogContent
        );

        await Promise.all(
          bookCmi5.auOrBlock.map(
            async unit =>
              await Promise.all([
                FileSystemService.saveOrUpdateOfflineComponents(unit, bookContext),
                FileSystemService.saveOrUpdateResources(unit, bookContext)
              ])
          )
        ).catch(e => {
          console.log(`Couldn't update components or resources. Error is ${e}`);
        });

        let contentBookOrUnitIsDownloaded = FileSystemService.isBookContentDownloaded(bookContext, bookCmi5);
        if (singleUnitModeUnitId) {
          const unitContext = contentUtils.createUnitContext(
            userId,
            classroomId,
            book.id,
            book.activityId,
            singleUnitModeUnitId,
            bookName
          );
          contentBookOrUnitIsDownloaded = FileSystemService.isUnitContentDownloaded(unitContext, bookCmi5);
        }

        if (contentBookOrUnitIsDownloaded) {
          contentBook.status = BOOK_DOWNLOADING_STATUS.COMPLETED;
          dispatch(createOrUpdateContentBook(contentBook));
        }

        if (!isAppOnline || !isElectronApp() || _.isEmpty(authToken)) {
          dispatch(updateContentBookIsSynchronizing(contentBook, false));
          return;
        }
      }

      const contentBooks = getState().content.contentBooks;
      const isDownloadingInStore = contentUtils.isStoredBookDownloading(contentBooks, bookContext);
      if (!bookCmi5 || isDownloadingInStore || !bookDownloadedOrInProgress) {
        dispatch(updateContentBookIsSynchronizing(contentBook, false));
        return;
      }

      const { hasUpdates, removedActivities } = FileSystemService.isBookOutdated(bookCmi5, bookContext);

      if (hasUpdates.length > 0 || removedActivities.length > 0) {
        FileSystemService.removeResources(removedActivities);
        dispatch(updateContentBookIsSynchronizing(contentBook, true));
        console.info('UPDATING BOOK-> ', bookName);
        await dispatch(saveBook(classroomId, book, bookName, false, hasUpdates, bookCmi5));
        if (unitActivityId) {
          contentUtils.notifyAuthoringToContentWindow(unitActivityId, bookContext);
        }
        dispatch(updateContentBookIsSynchronizing(contentBook, false));
      } else {
        console.info('ALREADY UPDATED BOOK');
        dispatch(updateContentBookIsSynchronizing(contentBook, false));
      }
    } catch (err) {
      console.error(err);
      dispatch(messageAlert.error(I18n.t('error.synchronizing')));
      dispatch(updateContentBookIsSynchronizing(contentBook, false));
      debug.error('[synchronizeBookDownload] Error', err);
    }
  };
}

function downloadContentResource(activityContext, activity, reDownloadBook = false) {
  return async (dispatch, getState) => {
    activityContext.customerId = getState().account.configuration.customerId;

    const activityName = translateObject(activity.title, getState().account.user.language);
    try {
      const account = getState().account;
      const school = getState().school.school;
      const activeClassrooms = getState().classrooms.activeClassrooms;
      const classroom = activeClassrooms.filter(classroom => classroom.id === activityContext.classId)[0];
      const activitiesContent = getState().content.activitiesContent;
      const isLesson = !!activityContext.lessonActivityId;
      const isContentAsset = !activity.format;

      if (isLesson) {
        await FileSystemService.createFolder(contentPathUtils.getLessonContentFolderPath(activityContext));
      }

      let activityContent = {};
      let activityContentId = contentUtils.getActivityContentId(activityContext, activity);
      if (activitiesContent[activityContentId] && !reDownloadBook) {
        activityContent = activitiesContent[activityContentId];
      } else {
        activityContent = contentUtils.createActivityContentStructure(activityContext, activity);
      }

      dispatch(setActivityContent(activityContent));

      const isAsset =
        (activity.activityType === ACTIVITY_TYPE.LESSON && !activity.fromCatalog) ||
        activity.resourceUrl.includes('assets');
      const { activityFilePath, activityFolderPath } = FileSystemService.getActivityContentFilePathsByMode(
        activityContext,
        activityContent.format
      );
      const downloadFilePath =
        activity.format === FILE_TYPES.ZIP.mimeType && !isAsset ? activityFilePath : activity.launcher;
      if (isContentAsset) {
        await dispatch(
          saveActivityContent(activity.url, activityFolderPath, activity.resourceUrl, activityContent, false)
        );
      } else {
        await dispatch(
          saveActivityContent(downloadFilePath, activityFolderPath, activity.resourceUrl, activityContent, isAsset)
        );
      }

      if (activity.mode === BOOK_LAUNCH_MODES.SMART) {
        const scaResponse = await contentUtils.postLtiParameters(activity.ltiOfflineUrl, account, school, classroom);

        if (scaResponse) {
          // Se sobrescribe el bookUri por la posibilidad de que sea una unidad importada libre
          scaResponse.bookUri = activityContext.bookActivityId;
          await contentUtils.saveSmartUnitScaResponse(activityContext, scaResponse, school.customer.id);
        }

        if (!isAsset) {
          await Promise.all([
            FileSystemService.saveOrUpdateOfflineComponents(activity, activityContext),
            FileSystemService.saveOrUpdateResources(activity, activityContext)
          ]).catch(e => {
            console.log(`Couldn't update components or resources. Error is ${e}`);
          });
        }

        await contentService.writeUnitDownloadCheckFile(activityContext);
      }
    } catch (error) {
      debug.error('[downloadContentResource] error saving content %s', error);
      const erroredContentBook = getState().content.contentBooks[activityContext.bookId];
      if (error.message === FAILED_TO_FETCH_ERROR) {
        dispatch(
          updateContentBookStatus(BOOK_DOWNLOADING_STATUS.PAUSE, activityContext.activityId, activityContext.classId)
        );
      } else if (erroredContentBook.status !== BOOK_DOWNLOADING_STATUS.CANCEL) {
        error.activityName = activityName;
        throw error;
      }
    }
  };
}

function saveActivityContent(destinationFilePath, destinationPath, resourceUrl, activityContent, isAsset) {
  return async (dispatch, getState) => {
    if (localStorage.getItem('cancel_' + activityContent.bookId)) {
      localStorage.removeItem('cancel_' + activityContent.bookId);
    }
    const repoUnitFileName = resourceUrl.substring(resourceUrl.lastIndexOf('/') + 1);

    const localRepoUnitPath = contentUtils.getLocalRepoUnitPath(getState().router.location, repoUnitFileName);
    const unitInLocalRepo = FileSystemService.existsFile(localRepoUnitPath);

    if (unitInLocalRepo) {
      console.info(
        `Downloading activity content from local repo: ${activityContent.activityId} - ${localRepoUnitPath}`
      );
      await dispatch(saveActivityContentFromLocalRepo(localRepoUnitPath, destinationPath, activityContent, isAsset));
    } else {
      const url = resourceUrl;
      console.info(
        `Downloading activity content from remote repo: ${activityContent.activityContentId} - ${resourceUrl}`
      );
      await dispatch(
        saveActivityContentFromRemoteRepo(url, destinationFilePath, destinationPath, activityContent, isAsset)
      );
    }
  };
}

function updateSkinAndCdn(authToken, userId, customerId, forceCdnDownload) {
  const context = { userId };
  return async (dispatch, getState) => {
    if (
      !getState().connection.status.online ||
      CDN_SYNCHRONIZATION_STATUS.SYNCHRONIZING === getState().content.cdnSynchronizationStatus
    ) {
      debug('[updateSkinAndCdn] Already synchronizing CDN');
      return Promise.resolve();
    }
    sendDownloadingOrUnzippingCdnOrSkin(true);
    await dispatch(setCdnSynchronization(CDN_SYNCHRONIZATION_STATUS.SYNCHRONIZING));
    return Promise.all([
      FileSystemService.synchronizeCdn(authToken, context, forceCdnDownload),
      FileSystemService.synchronizeSkin(authToken, customerId)
    ])
      .then(() => {
        dispatch(setCdnSynchronization(CDN_SYNCHRONIZATION_STATUS.COMPLETED));
        sendDownloadingOrUnzippingCdnOrSkin(false);
        debug('[CDN and Skin are synchronized');
      })
      .catch(error => {
        dispatch(setCdnSynchronization(CDN_SYNCHRONIZATION_STATUS.EMPTY));
        sendDownloadingOrUnzippingCdnOrSkin(false);
        debug.error('[updateSkinAndCdn] %s', error);
      });
  };
}

function saveActivityContentFromLocalRepo(resourceUrl, fileName, activityContent) {
  return async dispatch => {
    const stat = fs.statSync(resourceUrl);
    activityContent.totalSizeToDownload = stat.size;
    activityContent.downloadPercentage = DOWNLOAD.FINISHED;

    dispatch(setActivityContent(activityContent));

    return dispatch(contentService.unZipActivityContentWithPercentage(resourceUrl, fileName, activityContent, false));
  };
}

function saveBookContents(cmi5Units, bookContext, singleUnitModeUnitId, reDownloadBook, resourcesToUpdate) {
  return async (dispatch, getState) => {
    try {
      const foldersCreation = [];
      const resources = [];
      const downloadProvidedResources = resourcesToUpdate.length > 0;
      cmi5Units.forEach(unit => {
        const unitContext = Object.assign({ unitActivityId: unit.id }, bookContext);
        let unitFolderPath = contentPathUtils.getUnitFolderPath(unitContext);
        if (!FileSystemService.existsFolder(unitFolderPath)) {
          foldersCreation.push(FileSystemService.createFolder(unitFolderPath));
        }

        if (unit.resources) {
          const unitResources = unit.resources;
          for (const key in unitResources) {
            const resource = unitResources[key];
            if (!downloadProvidedResources || (resource && existResourceInArray(resource.id, resourcesToUpdate))) {
              resources.push({ context: unitContext, activity: resource, reDownloadBook });
            }
          }
        }

        if (unit.resourceUrl) {
          let authoringResourcesFolderPath = contentPathUtils.getAuthoringResourcesFolderPath(unitContext);
          if (!FileSystemService.existsFolder(authoringResourcesFolderPath)) {
            foldersCreation.push(FileSystemService.createFolder(authoringResourcesFolderPath));
          }
          if (downloadProvidedResources) {
            if (existResourceInArray(unit.id, resourcesToUpdate)) {
              resources.push({ context: unitContext, activity: unit, reDownloadBook });
            }
          } else {
            resources.push({ context: unitContext, activity: unit, reDownloadBook });
          }
        }

        unit.auOrBlock.forEach(lesson => {
          const lessonContext = Object.assign({ lessonActivityId: lesson.id }, unitContext);

          if ((!lesson.fromCatalog || lesson.resourceUrl) && lesson.format !== FILE_TYPES.LNK.mimeType) {
            let lessonFolderPath = contentPathUtils.getLessonContentFolderPath(lessonContext);
            if (!FileSystemService.existsFolder(lessonFolderPath)) {
              foldersCreation.push(FileSystemService.createFolder(lessonFolderPath));
            }
            if (downloadProvidedResources) {
              if (existResourceInArray(lesson.id, resourcesToUpdate)) {
                resources.push({ context: lessonContext, activity: lesson, reDownloadBook });
              }
            } else {
              resources.push({ context: lessonContext, activity: lesson, reDownloadBook });
            }
          }
        });
      });
      await Promise.all(foldersCreation);
      for (const res of resources) {
        const { context, activity, reDownloadBook } = res;
        await dispatch(downloadContentResource(context, activity, reDownloadBook));
      }
      const contentBook = getState().content.contentBooks[bookContext.bookId];

      await dispatch(updateContentBookIsSynchronizing(contentBook, false));
      await dispatch(bookContentDownloaded(bookContext, singleUnitModeUnitId));
    } catch (error) {
      debug.error('[saveBookContents] Error saving book contents %s', error);
      throw error;
    }
  };
}

function existResourceInArray(id, arrayResource) {
  let result = false;
  arrayResource.forEach(resource => {
    if (id === resource.id) {
      result = true;
      return;
    }
  });
  return result;
}

function saveActivityContentFromRemoteRepo(resourceUrl, responseFilePath, destinationPath, activityContent, isAsset) {
  return async (dispatch, getState) => {
    return new Promise(async (resolve, reject) => {
      const onStart = (size, percentage) => {
        activityContent.totalSizeToDownload = size;
        activityContent.downloadPercentage = percentage;
        activityContent.downloadStatus = BOOK_DOWNLOADING_STATUS.DOWNLOADING;
        dispatch(setActivityContent(activityContent));
      };

      const onData = _.throttle((request, percentage) => {
        const token = localStorage.getItem('smartAuthToken');
        if (!token) return;

        activityContent.downloadPercentage = percentage;
        dispatch(setActivityContent(activityContent));
      }, 100);

      const onEnd = () => {
        const token = localStorage.getItem('smartAuthToken');
        if (!localStorage.getItem('cancel_' + activityContent.bookId)) {
          if (!token) return;
          if (activityContent.format === FILE_TYPES.ZIP.mimeType && !isAsset) {
            dispatch(
              contentService.unZipActivityContentWithPercentage(
                responseFilePath,
                destinationPath,
                activityContent,
                true
              )
            )
              .then(resolve)
              .catch(reject);
          } else {
            activityContent.downloadStatus = BOOK_DOWNLOADING_STATUS.COMPLETED;
            dispatch(setActivityContent(activityContent));
            resolve({});
          }
        } else {
          localStorage.removeItem('cancel_' + activityContent.bookId);
        }
      };

      const onError = err => {
        console.error(err);
      };

      let authToken;
      let contentType;
      if (isAsset) {
        authToken = getTokenFromStorage();
        contentType = activityContent.format;
        const response = await axios.get(resourceUrl);
        resourceUrl = response.data.url;
      }
      const options = FileSystemService.httpRequestOptions({ url: resourceUrl, authToken, contentType });
      return FileSystemService.httpGetWithProgress(options, onStart, onData, onEnd, onError, responseFilePath);
    });
  };
}
